import { useState, type FormEvent } from 'react';
import CampoTexto from '@/components/base/CampoTexto';
import CampoFoto from '@/pages/inscricao/ficha/components/CampoFoto';
import {
  dataParaISO,
  mascaraCPF,
  mascaraData,
  mascaraTelefone,
  validarCPF,
} from '@/pages/inscricao/utils';
import { UFS } from '@/pages/assistente-evento/constants';
import { buscarEnderecoPorCep, mascaraCEP } from '@/lib/cep';

export interface DadosPessoais {
  nome: string;
  cpf: string;
  nascimento: string;
  telefone: string;
  cep: string;
  endereco: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  foto: File | null;
}

interface SeusDadosProps {
  nomeInicial: string;
  onSalvar: (dados: DadosPessoais) => Promise<void>;
  onPular: () => void;
}

interface Erros {
  nome?: string;
  cpf?: string;
  nascimento?: string;
  telefone?: string;
  cep?: string;
  endereco?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
}

const inputCls =
  'mt-1 w-full border-0 border-b bg-transparent px-0 py-2 text-[15px] text-foreground-950 outline-none transition-colors placeholder:text-secondary-400 focus:border-b-2 focus:border-primary-500';

export default function SeusDados({ nomeInicial, onSalvar, onPular }: SeusDadosProps) {
  const [nome, setNome] = useState(nomeInicial);
  const [cpf, setCpf] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cep, setCep] = useState('');
  const [endereco, setEndereco] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [uf, setUf] = useState('');
  const [foto, setFoto] = useState<File | null>(null);

  const [erros, setErros] = useState<Erros>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [avisoCep, setAvisoCep] = useState<string | null>(null);

  async function aoMudarCep(v: string) {
    setCep(mascaraCEP(v));
    setAvisoCep(null);
    setErros((e) => ({ ...e, cep: '' }));

    if (v.replace(/\D/g, '').length === 8) {
      setBuscandoCep(true);
      const achado = await buscarEnderecoPorCep(v);
      setBuscandoCep(false);
      if (achado) {
        setEndereco(achado.logradouro || '');
        setComplemento((prev) => prev || achado.complemento || '');
        setBairro(achado.bairro || '');
        setCidade(achado.localidade || '');
        setUf(achado.uf || '');
      } else {
        setAvisoCep('Não encontramos este CEP. Confira e preencha o endereço à mão.');
      }
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const novosErros: Erros = {};

    if (!nome.trim()) novosErros.nome = 'Digite seu nome completo.';
    if (!validarCPF(cpf)) novosErros.cpf = 'Digite um CPF válido.';
    if (!nascimento) novosErros.nascimento = 'Digite sua data de nascimento.';
    else if (nascimento.replace(/\D/g, '').length !== 8)
      novosErros.nascimento = 'Digite a data completa, como 13/09/2000.';
    if (telefone.replace(/\D/g, '').length < 10) novosErros.telefone = 'Digite o telefone, com DDD.';
    if (cep.replace(/\D/g, '').length !== 8) novosErros.cep = 'Digite o CEP completo.';
    if (!endereco.trim()) novosErros.endereco = 'Digite o endereço.';
    if (!bairro.trim()) novosErros.bairro = 'Digite o bairro.';
    if (!cidade.trim()) novosErros.cidade = 'Digite a cidade.';
    if (!uf) novosErros.uf = 'Escolha o estado.';

    setErros(novosErros);
    if (Object.keys(novosErros).length > 0) {
      setErroGeral(null);
      return;
    }

    setErroGeral(null);
    setSalvando(true);
    try {
      await onSalvar({
        nome: nome.trim(),
        cpf,
        nascimento: dataParaISO(nascimento),
        telefone,
        cep,
        endereco: endereco.trim(),
        complemento: complemento.trim(),
        bairro: bairro.trim(),
        cidade: cidade.trim(),
        uf,
        foto,
      });
    } catch {
      setErroGeral('Não conseguimos salvar seus dados. Tente de novo em instantes.');
      setSalvando(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
      <div className="w-full max-w-[560px] overflow-hidden rounded-lg border border-secondary-200 bg-background-50">
        <div className="h-[10px] w-full bg-primary-500" />
        <div className="p-6 sm:p-8">
          <div className="text-center">
            <span className="text-[22px] font-medium text-primary-500">Seus dados</span>
            <p className="mt-1 text-sm text-secondary-500">
              Preenchemos sua ficha com isto nas próximas inscrições. Você só digita uma vez.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate className="mt-6 animate-fade-in">
            <div className="space-y-6">
              <CampoTexto
                id="dados-nome"
                name="nome"
                label="Nome completo"
                value={nome}
                onChange={setNome}
                required
                error={erros.nome ?? null}
                autoComplete="name"
              />

              <CampoTexto
                id="dados-cpf"
                name="cpf"
                label="CPF"
                help="É por ele que identificamos você nos eventos."
                value={cpf}
                onChange={(v) => {
                  setCpf(mascaraCPF(v));
                  setErros((e) => ({ ...e, cpf: '' }));
                }}
                required
                error={erros.cpf ?? null}
                inputMode="numeric"
                placeholder="000.000.000-00"
                autoComplete="off"
              />

              <CampoTexto
                id="dados-nascimento"
                name="nascimento"
                label="Data de nascimento"
                value={nascimento}
                onChange={(v) => {
                  setNascimento(mascaraData(v));
                  setErros((e) => ({ ...e, nascimento: '' }));
                }}
                required
                error={erros.nascimento ?? null}
                inputMode="numeric"
                placeholder="DD/MM/AAAA"
                autoComplete="bday"
              />

              <CampoTexto
                id="dados-telefone"
                name="telefone"
                label="Telefone (WhatsApp)"
                value={telefone}
                onChange={(v) => {
                  setTelefone(mascaraTelefone(v));
                  setErros((e) => ({ ...e, telefone: '' }));
                }}
                required
                error={erros.telefone ?? null}
                inputMode="tel"
                placeholder="(00) 00000-0000"
                autoComplete="tel"
              />

              <div>
                <CampoTexto
                  id="dados-cep"
                  name="cep"
                  label="CEP"
                  help="Buscamos seu endereço sozinho a partir dele."
                  value={cep}
                  onChange={aoMudarCep}
                  required
                  error={erros.cep ?? null}
                  inputMode="numeric"
                  placeholder="00000-000"
                  autoComplete="postal-code"
                />
                {buscandoCep && (
                  <p className="mt-1 text-xs text-secondary-500">Buscando endereço…</p>
                )}
                {avisoCep && (
                  <p className="mt-1 text-sm text-secondary-600">{avisoCep}</p>
                )}
              </div>

              <CampoTexto
                id="dados-endereco"
                name="endereco"
                label="Endereço"
                value={endereco}
                onChange={(v) => {
                  setEndereco(v);
                  setErros((e) => ({ ...e, endereco: '' }));
                }}
                required
                error={erros.endereco ?? null}
                placeholder="Rua das Flores, 245"
                autoComplete="street-address"
              />

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <CampoTexto
                  id="dados-complemento"
                  name="complemento"
                  label="Complemento"
                  value={complemento}
                  onChange={setComplemento}
                  placeholder="Apto 12"
                />
                <CampoTexto
                  id="dados-bairro"
                  name="bairro"
                  label="Bairro"
                  value={bairro}
                  onChange={(v) => {
                    setBairro(v);
                    setErros((e) => ({ ...e, bairro: '' }));
                  }}
                  required
                  error={erros.bairro ?? null}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <CampoTexto
                  id="dados-cidade"
                  name="cidade"
                  label="Cidade"
                  value={cidade}
                  onChange={(v) => {
                    setCidade(v);
                    setErros((e) => ({ ...e, cidade: '' }));
                  }}
                  required
                  error={erros.cidade ?? null}
                />
                <div>
                  <label htmlFor="dados-uf" className="block text-base font-medium text-foreground-950">
                    Estado <span className="text-danger-600">*</span>
                  </label>
                  <select
                    id="dados-uf"
                    name="uf"
                    value={uf}
                    onChange={(e) => {
                      setUf(e.target.value);
                      setErros((e) => ({ ...e, uf: '' }));
                    }}
                    className={`${inputCls} cursor-pointer ${erros.uf ? 'border-danger-500' : 'border-secondary-300'}`}
                  >
                    <option value="">Selecione</option>
                    {UFS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  {erros.uf && (
                    <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                      {erros.uf}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-base font-medium text-foreground-950">Foto do rosto</p>
                <p className="mt-1 text-sm text-secondary-500">
                  Opcional. Você pode adicionar depois.
                </p>
                <div className="mt-2">
                  <CampoFoto arquivo={foto} erro={null} aoSelecionar={setFoto} />
                </div>
              </div>
            </div>

            {erroGeral && (
              <p className="mt-4 text-sm text-danger-600" role="alert">
                {erroGeral}
              </p>
            )}

            <button
              type="submit"
              disabled={salvando}
              className="mt-6 flex h-12 w-full items-center justify-center rounded-md bg-primary-500 text-[15px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400 whitespace-nowrap"
            >
              {salvando ? 'Salvando…' : 'Salvar e continuar'}
            </button>

            <button
              type="button"
              onClick={onPular}
              disabled={salvando}
              className="mt-3 w-full cursor-pointer whitespace-nowrap text-center text-sm text-secondary-500 transition-colors hover:text-foreground-700 disabled:cursor-not-allowed"
            >
              Preencher depois
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}